import type { WidgetConfig } from "./schema";

interface WidgetRow {
  row: number;
  widgets: WidgetConfig[];
}

export function groupByRow(widgets: WidgetConfig[]): WidgetRow[] {
  const sorted = [...widgets].sort((a, b) => {
    if (a.row !== b.row) return a.row - b.row;
    if (a.column !== b.column) return a.column - b.column;
    return a.id.localeCompare(b.id);
  });
  const map = new Map<number, WidgetConfig[]>();
  for (const w of sorted) {
    const arr = map.get(w.row) ?? [];
    arr.push(w);
    map.set(w.row, arr);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a - b)
    .map(([row, ws]) => ({ row, widgets: ws }));
}

/**
 * Re-numbers row/column so they're contiguous (rows start at 0, columns within
 * each row start at 0). Pure; used after drag-drop or delete.
 */
export function rebalance(widgets: WidgetConfig[]): WidgetConfig[] {
  const rows = groupByRow(widgets);
  const out: WidgetConfig[] = [];
  rows.forEach((r, rowIdx) => {
    r.widgets.forEach((w, colIdx) => {
      out.push({ ...w, row: rowIdx, column: colIdx });
    });
  });
  return out;
}

export function maxRow(widgets: WidgetConfig[]): number {
  return widgets.reduce((m, w) => Math.max(m, w.row), -1);
}

type DropTarget =
  | { kind: "slot"; row: number; column: number }
  | { kind: "new-row"; row: number };

export function parseDropId(id: string): DropTarget | null {
  if (id.startsWith("slot-row-")) {
    const [, , r, , c] = id.split("-");
    return { kind: "slot", row: Number(r), column: Number(c) };
  }
  if (id.startsWith("new-row-")) {
    const [, , r] = id.split("-");
    return { kind: "new-row", row: Number(r) };
  }
  return null;
}

export function moveWidget(
  widgets: WidgetConfig[],
  movedId: string,
  target: DropTarget,
): WidgetConfig[] {
  const moved = widgets.find((w) => w.id === movedId);
  if (!moved) return widgets;
  const rest = widgets.filter((w) => w.id !== movedId);

  if (target.kind === "new-row") {
    const bumped = rest.map((w) =>
      w.row >= target.row ? { ...w, row: w.row + 1 } : w,
    );
    return rebalance([...bumped, { ...moved, row: target.row, column: 0 }]);
  }

  const sameRow = rest.filter((w) => w.row === target.row);
  const others = rest.filter((w) => w.row !== target.row);
  const reordered = [
    ...sameRow.slice(0, target.column).map((w, i) => ({ ...w, column: i })),
    { ...moved, row: target.row, column: target.column },
    ...sameRow
      .slice(target.column)
      .map((w, i) => ({ ...w, column: target.column + 1 + i })),
  ];
  return rebalance([...others, ...reordered]);
}
